import { BundleProduct } from '../../store/bundleProduct/api/interface';

export type BundleContentMode = 'components' | 'variants';

type BundleContentCounts = Pick<
  BundleProduct,
  | 'itemsCount'
  | 'variantGroupsCount'
  | 'components'
  | 'variantGroups'
  | 'componentsActiveState'
  | 'variantsActiveState'
>;

export const isProductActive = (
  activeState: boolean | string | undefined
): boolean => activeState === true || String(activeState) === 'true';

export const getBundleComponentCount = (item: BundleContentCounts): number =>
  item.itemsCount ?? item.components?.length ?? 0;

export const getBundleVariantGroupCount = (item: BundleContentCounts): number =>
  item.variantGroupsCount ?? item.variantGroups?.length ?? 0;

export const resolveBundleContentMode = (
  item: BundleContentCounts,
  activeTab?: number
): BundleContentMode => {
  const componentCount = getBundleComponentCount(item);
  const variantGroupCount = getBundleVariantGroupCount(item);

  if (isProductActive(item.variantsActiveState) && variantGroupCount > 0) {
    return 'variants';
  }

  if (isProductActive(item.componentsActiveState) && componentCount > 0) {
    return 'components';
  }

  if (variantGroupCount > 0 && componentCount === 0) {
    return 'variants';
  }

  if (componentCount > 0) {
    return 'components';
  }

  if (activeTab === 1) {
    return 'variants';
  }

  return 'components';
};

/** Product visibility — controlled only by General Information status */
export const getBundleListingActiveState = (item: BundleProduct): boolean =>
  isProductActive(item.activeState);

export const normalizeBundleModeStates = (
  componentsActiveState: boolean,
  variantsActiveState: boolean
): {
  componentsActiveState: boolean;
  variantsActiveState: boolean;
} => {
  if (componentsActiveState && variantsActiveState) {
    return {
      componentsActiveState: true,
      variantsActiveState: false,
    };
  }

  if (!componentsActiveState && !variantsActiveState) {
    return {
      componentsActiveState: true,
      variantsActiveState: false,
    };
  }

  return {
    componentsActiveState,
    variantsActiveState,
  };
};

export const setBundleComponentsModeActive = (): {
  componentsActiveState: boolean;
  variantsActiveState: boolean;
} => ({
  componentsActiveState: true,
  variantsActiveState: false,
});

export const setBundleVariantsModeActive = (): {
  componentsActiveState: boolean;
  variantsActiveState: boolean;
} => ({
  componentsActiveState: false,
  variantsActiveState: true,
});

/** Keep tab statuses aligned when bundle has no components or variants yet */
export const syncEmptyBundleStatusFields = (
  activeState: boolean
): {
  activeState: boolean;
  componentsActiveState: boolean;
  variantsActiveState: boolean;
} => ({
  activeState,
  componentsActiveState: true,
  variantsActiveState: false,
});
